import axios from "axios";
import { toast } from "sonner";

// Ungesetzt/leer = same-origin: nginx proxyt /api an das Backend weiter.
// Nur setzen, wenn das Backend unter einer anderen Domain läuft.
export const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
    timeout: 15000,
});

// Woher der Token kommt, entscheidet auth.js — hier steht nur der Haken dafür.
// So bleibt der Speicherort an einer Stelle, und dieses Modul muss nichts
// importieren, was seinerseits dieses Modul importiert.
let tokenHolen = () => null;
export const setTokenHolder = (fn) => { tokenHolen = fn; };

api.interceptors.request.use((cfg) => {
    const t = tokenHolen();
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
    return cfg;
});

// Selbstheilung: verlorene/hängende GET-Requests einmal automatisch wiederholen
api.interceptors.response.use(undefined, async (error) => {
    const cfg = error.config || {};
    const isGet = (cfg.method || "").toLowerCase() === "get";
    const transient = error.code === "ECONNABORTED" || error.message === "Network Error";
    if (isGet && transient && !cfg.__retried) {
        cfg.__retried = true;
        return api.request(cfg);
    }
    // Sitzung vorbei. Zentral melden, damit nicht jede Seite ihren eigenen
    // Umgang damit erfindet — und nicht auf der Anmeldeseite selbst, dort ist
    // ein 401 die normale Antwort auf ein falsches Passwort.
    const istAnmeldung = (cfg.url || "").includes("/auth/login");
    if (error.response?.status === 401 && !istAnmeldung) {
        window.dispatchEvent(new Event("auth:abgelaufen"));
        toast.error("Die Sitzung ist abgelaufen", {
            description: "Bitte melde dich neu an. Nicht gespeicherte Eingaben gehen dabei verloren.",
        });
        return Promise.reject(error);
    }

    // Nur-Lese-Zugang. Die Oberfläche sperrt die Knöpfe bereits, aber nicht
    // jede Stelle ist erfasst — hier landet, was durchgerutscht ist. Ohne diese
    // Meldung klickt jemand auf Speichern und sieht schlicht nichts passieren.
    if (error.response?.status === 403) {
        const d = error.response.data?.detail;
        toast.error("Nicht gespeichert — dein Zugang darf das nicht", {
            description: typeof d === "string" ? d
                : "Diese Änderung braucht mehr Rechte. Deine Eingaben stehen noch im Formular.",
            duration: 10000,
        });
        return Promise.reject(error);
    }

    // Schreibkonflikt: der Datensatz wurde zwischenzeitlich von jemand anderem
    // geändert. Hier zentral melden, weil sonst Speicher-Funktionen ohne
    // catch-Zweig den Fehler verschlucken — der Nutzer klickt auf Speichern und
    // sieht nichts, während seine Eingaben nicht ankommen.
    if (error.response?.status === 409 && error.response.data?.detail?.current_version) {
        toast.error("Nicht gespeichert — der Datensatz wurde zwischenzeitlich geändert", {
            description:
                "Jemand anderes hat diesen Datensatz bearbeitet, während du daran warst. " +
                "Deine Eingaben stehen noch im Formular. Lade die Seite neu, um den " +
                "aktuellen Stand zu sehen — dabei gehen sie verloren, notiere sie also vorher.",
            duration: 15000,
        });
    }
    return Promise.reject(error);
});

// Version aus der Nutzlast in den If-Match-Header umziehen. Die geladenen
// Datensätze tragen sie bereits, deshalb müssen die Seiten nichts mitgeben —
// sie dürfen sie beim Aufbau des Formulars nur nicht wegwerfen. Fehlt sie,
// schreibt das Backend ohne Prüfung; das ist für Skripte gewollt, in der
// Oberfläche aber ein Fehler, deshalb die Warnung.
const withVersion = (payload, was) => {
    const { version, ...rest } = payload || {};
    if (version === undefined || version === null) {
        console.warn(
            `[api] ${was}: kein 'version'-Feld in der Nutzlast — ohne If-Match ` +
            `überschreibt dieser Aufruf fremde Änderungen ungeprüft.`,
        );
        return [rest, {}];
    }
    return [rest, { headers: { "If-Match": String(version) } }];
};

// Entities
export const listEntities = () => api.get("/entities").then((r) => r.data);
export const getEntity = (id) => api.get(`/entities/${id}`).then((r) => r.data);
export const createEntity = (payload) => api.post("/entities", payload).then((r) => r.data);
export const updateEntity = (id, payload) => {
    const [body, cfg] = withVersion(payload, "Entität");
    return api.put(`/entities/${id}`, body, cfg).then((r) => r.data);
};
export const deleteEntity = (id, cascade = false) => api.delete(`/entities/${id}?cascade=${cascade}`).then((r) => r.data);

// Properties (Wirtschaftseinheiten)
// Dokumente — Verweise auf Dateien im Google Drive. Die Dateien selbst
// bleiben dort; hier laufen nur Verweis, Art, Jahr und Sphaere.
export const listDokumente = (params = {}) =>
    api.get("/dokumente", { params }).then((r) => r.data);
export const dokumentArten = () =>
    api.get("/dokumente/arten").then((r) => r.data);
export const dokumenteStatus = () =>
    api.get("/dokumente/status").then((r) => r.data);
// "Verzeichnen" und nicht "einlesen": gelesen werden die Eigenschaften der
// Dateien, nie ihr Inhalt.
export const dokumenteVerzeichnen = ({ liegenschaftId, ordnerId, apply = false,
                                      aktualisieren = false } = {}) =>
    api.post("/dokumente/verzeichnen", {}, {
        params: { liegenschaft_id: liegenschaftId, ordner_id: ordnerId, apply, aktualisieren },
        timeout: 180000,   // ein Ordner mit mehreren hundert Dateien braucht laenger
    }).then((r) => r.data);

// Liegenschaften — die Klammer ueber den Objekten. Teilungserklaerung,
// Beschluesse und Verwaltervertrag gelten fuer die Liegenschaft, nicht fuer
// die einzelne Wohnung.
export const listLiegenschaften = (params = {}) =>
    api.get("/liegenschaften", { params }).then((r) => r.data);
export const getLiegenschaft = (id) =>
    api.get(`/liegenschaften/${id}`).then((r) => r.data);
export const createLiegenschaft = (payload) =>
    api.post("/liegenschaften", payload).then((r) => r.data);
export const updateLiegenschaft = (id, payload) =>
    api.put(`/liegenschaften/${id}`, payload).then((r) => r.data);
export const deleteLiegenschaft = (id) =>
    api.delete(`/liegenschaften/${id}`).then((r) => r.data);
export const liegenschaftZustaende = () =>
    api.get("/liegenschaften/zustaende").then((r) => r.data);
// Die Benennungsregel liegt im Backend, damit die Oberflaeche sie nicht nachbaut.
export const liegenschaftNamensvorschlag = (payload) =>
    api.post("/liegenschaften/namensvorschlag", payload).then((r) => r.data);
export const liegenschaftVorschlag = ({ apply = false } = {}) =>
    api.post("/liegenschaften/vorschlag", {}, { params: { apply } }).then((r) => r.data);

export const listProperties = (params = {}) => api.get("/properties", { params }).then((r) => r.data);
export const getProperty = (id) => api.get(`/properties/${id}`).then((r) => r.data);
export const createProperty = (payload) => api.post("/properties", payload).then((r) => r.data);
export const updateProperty = (id, payload) => {
    const [body, cfg] = withVersion(payload, "Immobilie");
    return api.put(`/properties/${id}`, body, cfg).then((r) => r.data);
};
export const deleteProperty = (id, cascade = false) => api.delete(`/properties/${id}?cascade=${cascade}`).then((r) => r.data);

// Units (Mieteinheiten)
export const listUnits = (params = {}) => api.get("/units", { params }).then((r) => r.data);
export const getUnit = (id) => api.get(`/units/${id}`).then((r) => r.data);
export const createUnit = (payload) => api.post("/units", payload).then((r) => r.data);
export const updateUnit = (id, payload) => {
    const [body, cfg] = withVersion(payload, "Mieteinheit");
    return api.put(`/units/${id}`, body, cfg).then((r) => r.data);
};
export const deleteUnit = (id, cascade = false) => api.delete(`/units/${id}?cascade=${cascade}`).then((r) => r.data);

// Tenants
export const listTenants = (params = {}) => api.get("/tenants", { params }).then((r) => r.data);
export const getTenant = (id) => api.get(`/tenants/${id}`).then((r) => r.data);
export const createTenant = (payload) => api.post("/tenants", payload).then((r) => r.data);
export const updateTenant = (id, payload) => {
    const [body, cfg] = withVersion(payload, "Mieter");
    return api.put(`/tenants/${id}`, body, cfg).then((r) => r.data);
};
export const deleteTenant = (id, cascade = false) => api.delete(`/tenants/${id}?cascade=${cascade}`).then((r) => r.data);

// Status-Tracker (Lifecycle + Tasks pro Einheit)
export const getTracker = (unitId) => api.get(`/units/${unitId}/tracker`).then((r) => r.data);
export const patchTracker = (unitId, payload) => api.patch(`/units/${unitId}/tracker`, payload).then((r) => r.data);
export const deleteTrackerHistory = (unitId, entryId) => api.delete(`/units/${unitId}/tracker/history/${entryId}`).then((r) => r.data);
export const addTrackerTask = (unitId, payload) => api.post(`/units/${unitId}/tracker/tasks`, payload).then((r) => r.data);
export const patchTrackerTask = (unitId, taskId, payload) => api.patch(`/units/${unitId}/tracker/tasks/${taskId}`, payload).then((r) => r.data);
export const deleteTrackerTask = (unitId, taskId) => api.delete(`/units/${unitId}/tracker/tasks/${taskId}`).then((r) => r.data);
export const getEntityTrackerDashboard = (entityId) => api.get(`/entities/${entityId}/tracker-dashboard`).then((r) => r.data);
// Alle Aufgaben ueber alle Einheiten, mit Entitaet, Immobilie, Einheit und
// Mieter angereichert -- fuer die Aufgabenliste und die Cockpit-Ansicht.
export const listAufgaben = (params = {}) => api.get("/aufgaben", { params }).then((r) => r.data);
// Was ansteht, aus den Ampeln der Module -- die Startseite rechnet nichts.
export const arbeitsliste = () => api.get("/arbeitsliste").then((r) => r.data);
// Die sieben Konsistenz-Pruefungen: wo der Bestand sich selbst widerspricht.
export const listWidersprueche = () =>
    api.get("/mietkonto/widersprueche").then((r) => r.data);
// Durchsicht der Vorschlaege: gleichfoermige Gruppen, sichere Einzelne, Rest.
export const vorschlaegeDurchsicht = (jahr = 2026) =>
    api.get("/mietkonto/vorschlaege/durchsicht", { params: { jahr } }).then((r) => r.data);
// Sammelbestaetigung. Jeder Lauf bekommt eine Kennung, damit er als Ganzes
// zurueckgenommen werden kann.
export const allocationsBestaetigen = (allocationIds, { apply = false, grund = null } = {}) =>
    api.post("/mietkonto/allocations/bestaetigen", { allocation_ids: allocationIds, grund },
             { params: { apply } }).then((r) => r.data);
export const sammelZuruecknehmen = (lauf) =>
    api.post(`/mietkonto/allocations/lauf/${lauf}/zuruecknehmen`).then((r) => r.data);

// Turnovers
export const listTurnovers = (params = {}) => api.get("/turnovers", { params }).then((r) => r.data);
export const getTurnover = (id) => api.get(`/turnovers/${id}`).then((r) => r.data);
export const createTurnover = (payload) => api.post("/turnovers", payload).then((r) => r.data);
export const updateTurnover = (id, payload) => {
    const [body, cfg] = withVersion(payload, "Mieterwechsel");
    return api.put(`/turnovers/${id}`, body, cfg).then((r) => r.data);
};
export const advanceTurnover = (id) => api.post(`/turnovers/${id}/advance`).then((r) => r.data);
export const deleteTurnover = (id) => api.delete(`/turnovers/${id}`).then((r) => r.data);

// Cockpit
export const getCockpitStats = () => api.get("/stats/cockpit").then((r) => r.data);
// Die steuernde Zahl des Fahrplans: Anteil der erklaerten Soll-Positionen
// eines Jahres (bestaetigt gedeckt oder durch Klaerung anerkannt).
export const erklaert = (jahr = 2026) =>
    api.get("/mietkonto/erklaert", { params: { jahr } }).then((r) => r.data);
export const getDashboardStats = () => api.get("/stats/dashboard").then((r) => r.data);
export const seedDemo = () => api.post("/seed").then((r) => r.data);

// Bestand-Import (Excel pro Entität)
export const templateDownloadUrl = `${API}/import/template.xlsx`;
export const importBestand = (entityId, file, dryRun = false) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/entities/${entityId}/import?dry_run=${dryRun ? "true" : "false"}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
};

// Wiki (globale & immobilienspezifische Notizen mit Sprach-Diktat)
export const listWiki = (params = {}) => api.get("/wiki", { params }).then((r) => r.data);
export const listWikiTags = () => api.get("/wiki/tags").then((r) => r.data);
export const getWiki = (id) => api.get(`/wiki/${id}`).then((r) => r.data);
export const createWiki = (payload) => api.post("/wiki", payload).then((r) => r.data);
export const updateWiki = (id, payload) => api.put(`/wiki/${id}`, payload).then((r) => r.data);
export const deleteWiki = (id) => api.delete(`/wiki/${id}`).then((r) => r.data);
export const dictateWiki = (audioBlob, scope, scopeId = null, filename = "audio.webm") => {
    const fd = new FormData();
    fd.append("audio", audioBlob, filename);
    fd.append("scope", scope);
    if (scopeId) fd.append("scope_id", scopeId);
    return api.post("/wiki/dictate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,  // 2 min for large audio + LLM processing
    }).then((r) => r.data);
};

// Telegram Bot Diagnose
export const getTelegramStatus = () => api.get("/telegram/status").then((r) => r.data);

// Wiki-Anhänge
export const listWikiAttachments = (entryId) =>
    api.get(`/wiki/${entryId}/attachments`).then((r) => r.data);

export const uploadWikiAttachment = (entryId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/wiki/${entryId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
    }).then((r) => r.data);
};

export const deleteWikiAttachment = (attachmentId) =>
    api.delete(`/wiki/attachments/${attachmentId}`).then((r) => r.data);

export const wikiAttachmentUrl = (attachmentId) =>
    `${API}/wiki/attachments/${attachmentId}/download`;

// Mietkonto — Soll gegen Ist
// Die Ampel kommt abgeleitet vom Server; hier wird nichts nachgerechnet, sonst
// laufen Oberflaeche und Backend auseinander.
export const generateCharges = (period, apply = false, tenantId = null) =>
    api.post(`/mietkonto/charges/generate`, null,
             { params: { period, apply, ...(tenantId ? { tenant_id: tenantId } : {}) } }).then((r) => r.data);
// Schritt 4a: regelmaessige Zahler ohne Mietverhaeltnis, als Vorschlaege.
// Nur lesend -- angelegt wird ueber createTenant, nachdem der Mensch bestaetigt hat.
export const fehlendeMietverhaeltnisse = (params = {}) =>
    api.get("/mietkonto/mietverhaeltnisse/vorschlag", { params }).then((r) => r.data);
// Zuordnungsvorschlag. Ohne period ueber alle offenen Perioden -- das ist der
// Regelfall, weil eine Zahlung auch einen alten Rueckstand tilgen darf.
// apply legt Status "vorschlag" an, nicht "bestaetigt": die Ampel bleibt rot,
// bis jemand hingesehen hat.
export const proposeAllocations = (period = null, apply = false) =>
    api.post("/mietkonto/allocations/vorschlag", null,
             { params: { ...(period ? { period } : {}), apply } }).then((r) => r.data);

// Bankkonten und Kautionen. Alle Vorschlaege sind Trockenlauf, geschrieben wird
// nur mit apply=true -- dieselbe Regel wie im Backend, hier nicht aufgeweicht.
// arten als Komma-Liste, z.B. "miete,kaution,unklar". Ohne Angabe kommt alles.
export const listKonten = (arten = null) =>
    api.get("/mietkonto/konten", { params: arten ? { arten } : {} }).then((r) => r.data);
export const kontenArten = () => api.get("/mietkonto/konten/arten").then((r) => r.data);
// Handkorrektur der Kontoart. Ohne art wird sie zurueckgenommen und es gilt
// wieder die Erkennung.
export const setKontoArt = (iban, art, grund = null) =>
    api.patch(`/mietkonto/konten/${encodeURIComponent(iban)}/art`, { art, grund })
        .then((r) => r.data);
export const resetKontoArt = (iban) =>
    api.delete(`/mietkonto/konten/${encodeURIComponent(iban)}/art`).then((r) => r.data);
export const proposeKonten = () =>
    api.post("/mietkonto/konten/vorschlag").then((r) => r.data);
export const assignKonten = (zuordnungen, { apply = false, force = false } = {}) =>
    api.post("/mietkonto/konten/zuordnen", { zuordnungen, force }, { params: { apply } })
        .then((r) => r.data);
// Ohne Nutzlast wird der Vermerk entfernt -- das Konto ist dann wieder ein Fund.
export const setKontoAbschluss = (iban, payload = null) =>
    api.patch(`/mietkonto/konten/${encodeURIComponent(iban)}/abschluss`, payload)
        .then((r) => r.data);

export const listKautionen = () => api.get("/mietkonto/kautionen").then((r) => r.data);
export const proposeKautionen = () =>
    api.post("/mietkonto/kautionen/vorschlag").then((r) => r.data);
export const assignKautionen = (zuordnungen, { apply = false } = {}) =>
    api.post("/mietkonto/kautionen/zuordnen", { zuordnungen }, { params: { apply } })
        .then((r) => r.data);

export const importAuszug = (datei, apply = false) => {
    const form = new FormData();
    form.append("datei", datei);
    // Der Content-Type MUSS hier stehen: die Instanz oben setzt global
    // "application/json", und damit deklariert der Browser den Multipart-Body
    // falsch -- FastAPI findet das Feld "datei" nicht und antwortet 422. Genau
    // das ist am 03.10.2026 passiert, und weil der Interceptor 422 nicht
    // behandelt, blieb der Dialog stumm stehen. Die drei anderen Uploads in
    // dieser Datei setzen den Header aus demselben Grund.
    return api.post("/mietkonto/import", form, {
        params: { apply },
        headers: { "Content-Type": "multipart/form-data" },
        // Ein Jahresexport bringt mehrere tausend Zeilen mit; beim Schreiben
        // kommen Konten-Upserts dazu. Die 15 s der Instanz sind dafuer zu knapp.
        timeout: 120000,
    }).then((r) => r.data);
};

export const deleteCharge = (chargeId) =>
    api.delete(`/mietkonto/charges/${chargeId}`).then((r) => r.data);
export const listCharges = (params = {}) =>
    api.get("/mietkonto/charges", { params }).then((r) => r.data);
// Wirksam gewordene Mietanpassungen in die Mietverhaeltnisse schreiben.
// Trockenlauf als Standard. Taeglich laeuft dasselbe aus dem Cron.
export const mietanpassungNachziehen = ({ apply = false } = {}) =>
    api.post("/mietanpassungen/nachziehen", null, { params: { apply } }).then((r) => r.data);
// Kontoauszug eines Mietverhaeltnisses: alle Forderungen und was darauf
// gezahlt wurde. Beantwortet "ist hier noch etwas saeumig", ohne dass man
// das Mietkonto Monat fuer Monat durchgehen muss.
export const mieterKonto = (tenantId) =>
    api.get(`/mietkonto/mieter/${tenantId}/konto`).then((r) => r.data);
// Einmalige Soll-Position, zuerst die Nebenkostenabrechnung (Auftrag 6).
// Trockenlauf als Standard, wie bei jedem schreibenden Werkzeug. Das
// Vorzeichen traegt die Richtung: positiv Nachzahlung, negativ Guthaben.
export const createCharge = (payload, { apply = false } = {}) =>
    api.post("/mietkonto/charges", payload, { params: { apply } }).then((r) => r.data);
export const setKlaerung = (chargeId, payload) => {
    // payload === null entfernt die Klaerung. Version als If-Match, wie bei den
    // uebrigen Schreibpfaden.
    const cfg = payload?.version ? { headers: { "If-Match": String(payload.version) } } : {};
    const body = payload ? (({ version, ...rest }) => rest)(payload) : null;
    return api.patch(`/mietkonto/charges/${chargeId}/klaerung`, body, cfg).then((r) => r.data);
};
export const listRentPayments = (params = {}) =>
    api.get("/mietkonto/payments", { params }).then((r) => r.data);
export const createRentPayment = (payload) =>
    api.post("/mietkonto/payments", payload).then((r) => r.data);
export const listAllocations = (params = {}) =>
    api.get("/mietkonto/allocations", { params }).then((r) => r.data);
export const createAllocation = (payload) =>
    api.post("/mietkonto/allocations", payload).then((r) => r.data);
export const confirmAllocation = (id) =>
    api.post(`/mietkonto/allocations/${id}/confirm`).then((r) => r.data);
export const deleteAllocation = (id) =>
    api.delete(`/mietkonto/allocations/${id}`).then((r) => r.data);
// Soll gegen Ist ueber ein Jahr: wer zahlt dauerhaft anders als vereinbart.
// Einen Eingang als "gehoert nicht zur Miete" kennzeichnen (Darlehen,
// Eigenkapital). Loescht nichts -- nimmt ihn nur aus der Kontrolle.
export const zahlungKeineMiete = (id, keine_miete = true, grund = null) =>
    api.patch(`/mietkonto/payments/${id}/keine-miete`, { keine_miete, grund })
        .then((r) => r.data);
export const mietkontoAbweichungen = (jahr) =>
    api.get("/mietkonto/abweichungen", { params: jahr ? { jahr } : {} })
        .then((r) => r.data);
export const mietkontoSummary = (period) =>
    api.get("/mietkonto/summary", { params: period ? { period } : {} }).then((r) => r.data);

// Zugaenge. Rollen: lesen < schreiben < verwalten. Die Namen sind absichtlich
// dieselben wie in der Nachbar-App "Aufteiler" -- zwei Apps mit verschiedenen
// Rollenmodellen zu betreiben verwirrt mehr als es nuetzt.
export const listUsers = () => api.get("/users").then((r) => r.data);
export const createUser = (payload) => api.post("/users", payload).then((r) => r.data);
export const updateUser = (id, payload) => api.patch(`/users/${id}`, payload).then((r) => r.data);
export const setUserPassword = (id, password) =>
    api.post(`/users/${id}/password`, { password }).then((r) => r.data);
export const deleteUser = (id) => api.delete(`/users/${id}`).then((r) => r.data);
export const changeOwnPassword = (alt, neu) =>
    api.post("/auth/password", { alt, neu }).then((r) => r.data);

// Aenderungshistorie. tiefe="feld" zeigt nur, was mit Werten protokolliert ist
// -- der uebliche Fall. Ohne Filter kommen die Zugriffs-Eintraege mit; die sind
// zahlreich und beantworten eine andere Frage ("wer hat das ueberhaupt
// angefasst").
export const listHistorie = (params = {}) =>
    api.get("/historie", { params }).then((r) => r.data);
export const historieObjekt = (objektId) =>
    api.get(`/historie/objekt/${objektId}`).then((r) => r.data);
export const historieBereiche = () =>
    api.get("/historie/bereiche").then((r) => r.data);

// --- Mietanpassungen -------------------------------------------------------
// Eine Anpassung sagt: ab diesem Monat gilt dieser Betrag. Die Soll-Erzeugung
// fragt danach, statt den Betrag am Mietverhältnis zu nehmen.
export const listMietanpassungen = (params = {}) =>
    api.get("/mietanpassungen", { params }).then((r) => r.data);
// Welche Miete gilt heute -- je Mietverhältnis, nur wo eine Anpassung
// greift. Die Listen kennen den Vertragsbetrag schon.
export const aktuelleMieten = (stichmonat) =>
    api.get("/mietanpassungen/aktuell", { params: stichmonat ? { stichmonat } : {} })
        .then((r) => r.data);
export const mietverlauf = (tenantId) =>
    api.get(`/mietanpassungen/verlauf/${tenantId}`).then((r) => r.data);
export const pruefeMietanpassung = (params) =>
    api.get("/mietanpassungen/pruefung", { params }).then((r) => r.data);
export const createMietanpassung = (payload) =>
    api.post("/mietanpassungen", payload).then((r) => r.data);
export const updateMietanpassung = (id, payload) =>
    api.patch(`/mietanpassungen/${id}`, payload).then((r) => r.data);
// Rueckwirkende Anpassung: schon erzeugte Soll-Positionen angleichen.
// Trockenlauf zuerst, wie bei allen schreibenden Werkzeugen hier.
export const nachberechnung = (payload = {}, apply = false) =>
    api.post(`/mietanpassungen/nachberechnung?apply=${apply}`, payload)
        .then((r) => r.data);
export const deleteMietanpassung = (id) =>
    api.delete(`/mietanpassungen/${id}`).then((r) => r.data);
